// HOME'S EXCEPTION QUEUE (UI rebuild batch 2; round 3 — the operator's ruling of 26 Oct: Astra's Home, in Astra's words)
// — ONE ROW PER KIND OF WORK, each with the one button that does it, in Astra's order:
//   approvals awaiting your signature (custody, ONLY when switched on)       [Review]   → Custody · Approvals
//   cheques to pay · cheques to deliver (open manager's cheques)              [Pay] / [Deliver] → the cheque
//   deals to settle later (the settle sweep's parties — Settle later is a term) [Settle] → Settle later, as it is
//   payments that need a match (chain transfers the books have no entry for) [Match]   → Chain (the row, when one)
// At most five rows (the five kinds). Two or more cheques of one kind open the rest inline ("+N more"): no other screen
// lists open cheques, so this is the only door to each one.
//
// THE SAFETY POINTS, IN THEIR LIGHTEST FORM (readState, design system Appendix B; Astra's own release checks):
//   · a failed source is ONE amber row with Try again (the server's words behind Details);
//   · a failed REFRESH keeps its old rows under that row, with no count in their titles (a cached count is no claim);
//   · a source still being read (or paused offline) is one "Reading …" row — never drawn as nothing to do;
//   · "N items" and "Nothing needs you." only after EVERY source read successfully;
//   · a desk with custody switched off has no custody row and no custody read (lib/routes.ts CUSTODY_IN_CONSOLE).
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeftRight, FileText, Link2, Lock, type LucideIcon } from 'lucide-react';
import { EmptyState, Money, Panel, Pending, ReadFailed, SkeletonRows } from '../kit';
import type { api, ApprovalRow } from '../../../lib/api';
import { readState, type ReadState } from '../../../lib/read-state';
import { partyName } from '../../../lib/naming';

type Mcs = Awaited<ReturnType<typeof api.pendingMcs>>;
type PendingMc = Mcs['pending'][number];
type Settle = Awaited<ReturnType<typeof api.settlements>>;
type Chain = Awaited<ReturnType<typeof api.chainActivity>>;
type Approvals = { approvals: ApprovalRow[] };

/** Chain transfers asked for with the chain read (its `counts` give the total; the newest names the row). Part of the
 *  chain read's query key (Home), as on Dashboard. */
export const CHAIN_GLANCE_ROWS = 3;


/** A title with its count when the read is current; without it when only a failed refresh's old rows are in hand. */
const counted = (stale: boolean, n: number, one: string, many: string, bare: string) =>
  stale ? bare : n === 1 ? one : `${n} ${many}`;

/** The row grid of the kit's TaskRow, for the two rows that are not a door. */
const ROW = 'grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:px-5';

const TONE = { neutral: 'bg-panel-2 text-tg-hint', warn: 'bg-warn-bg text-warn' } as const;

/** A TASK WITH ITS FIGURE IN ITS OWN SEAT (his "one fact per place", 1 Nov 2026): the kit's TaskRow — icon, what, who —
 *  with the amount right-aligned in mono before the button on the laptop, and on its own line under the name on the phone,
 *  never inside the name's sentence. */
function FigureTaskRow({ icon: Icon, tone = 'neutral', title, sub, figure, action, to }: {
  icon: LucideIcon; tone?: keyof typeof TONE; title: ReactNode; sub?: ReactNode; figure?: ReactNode; action: string; to: string;
}) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:grid-cols-[32px_minmax(0,1fr)_auto_auto] lg:px-5">
      <span className={`grid h-8 w-8 place-items-center rounded-lg ${TONE[tone]}`} aria-hidden><Icon className="h-4 w-4" /></span>
      <div className="min-w-0">
        <div className="text-body font-medium">{title}</div>
        {sub && <div className="mt-1 break-words text-meta text-tg-hint">{sub}</div>}
        {figure && <div className="mt-1 whitespace-nowrap text-body lg:hidden">{figure}</div>}
      </div>
      {figure ? <div className="whitespace-nowrap text-right text-body max-lg:hidden">{figure}</div> : <span className="max-lg:hidden" />}
      <Link to={to} className="btn-secondary px-3 text-body">{action}</Link>
    </div>
  );
}

/** A source that could not be read: one amber row, the server's words behind Details, and Try again. */
function FailedRow({ what, error, retry }: { what: string; error: unknown; retry: () => void }) {
  return (
    <div className={ROW}>
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-warn-bg text-warn" aria-hidden><AlertTriangle className="h-4 w-4" /></span>
      <ReadFailed what={what} error={error} className="min-w-0" />
      <button type="button" onClick={retry} className="btn-secondary px-3 text-body">Try again</button>
    </div>
  );
}

/** A source whose first read has not answered (or is paused offline). */
function WaitingRow({ what }: { what: string }) {
  return (
    <div className={ROW}>
      <span className="h-8 w-8 rounded-lg bg-panel-2" aria-hidden />
      <Pending inline what={`Reading ${what}…`} />
      <span />
    </div>
  );
}

/** THE ITEMS UNDER A TASK, AS ONE LINE OF SHORT LINKS (restored 1 Nov 2026, his "restore most the lost things", kept
 *  clean on Astra's review): up to three names, each opening its own item, then "+N" to the rest. Names only — the ages,
 *  balances and figures stay on Settle later and Chain. */
const NAMES_SHOWN = 3;
/** The phone names one (its line is narrow: a second figure would be cut by the ellipsis, and a figure is never cut). */
const NAMES_SHOWN_PHONE = 1;
function NameLinks({ items, total, more }: { items: Array<{ key: string; label: ReactNode; to: string }>; total: number; more: string }) {
  const rest = (shown: number) => total - Math.min(items.length, shown);
  return (
    // Names wrap, never cut (the audit at 1024, 1 Nov 2026: "Cedar I…", "Harbour Point Exp…").
    <span className="block break-words">
      {items.slice(0, NAMES_SHOWN).map((it, i) => (
        <span key={it.key} className={i >= NAMES_SHOWN_PHONE ? 'max-lg:hidden' : ''}>
          {i > 0 && ' · '}<Link to={it.to} className="text-tg-link hover:underline">{it.label}</Link>
        </span>
      ))}
      {rest(NAMES_SHOWN_PHONE) > 0 && <span className="lg:hidden"> · <Link to={more} className="text-tg-link hover:underline">+{rest(NAMES_SHOWN_PHONE)}</Link></span>}
      {rest(NAMES_SHOWN) > 0 && <span className="max-lg:hidden"> · <Link to={more} className="text-tg-link hover:underline">+{rest(NAMES_SHOWN)}</Link></span>}
    </span>
  );
}

/** What a source's rows may be drawn from: the read itself, or — after a failed refresh — its old rows, marked stale. */
function rowsOf<T>(q: UseQueryResult<T>, st: ReadState): { data: T | undefined; stale: boolean } {
  if (st === 'known') return { data: q.data, stale: false };
  if (st === 'failed' && q.data !== undefined) return { data: q.data, stale: true };
  return { data: undefined, stale: false };
}

export function AttentionQueue({ approvals, mcs, settle, chain, role, className }: {
  /** Custody's approvals — null while custody is switched off. */
  approvals: UseQueryResult<Approvals> | null;
  mcs: UseQueryResult<Mcs>;
  settle: UseQueryResult<Settle>;
  chain: UseQueryResult<Chain>;
  /** Who is looking, in the shell's words (Astra's badge at the head). */
  role?: string;
  className?: string;
}) {
  const sAppr = approvals ? readState(approvals) : null;
  const sMcs = readState(mcs);
  const sSettle = readState(settle);
  const sChain = readState(chain);

  const appr = approvals && sAppr ? rowsOf(approvals, sAppr) : { data: undefined, stale: false };
  const chq = rowsOf(mcs, sMcs);
  const stl = rowsOf(settle, sSettle);
  const chn = rowsOf(chain, sChain);

  const waiting = appr.data?.approvals ?? [];
  const cheques = chq.data?.pending ?? [];
  const toPay = cheques.filter((c) => c.lifecycle === 'mc_pending_seller');
  const toDeliver = cheques.filter((c) => c.lifecycle !== 'mc_pending_seller');
  const parties = stl.data?.parties ?? [];
  const deals = parties.reduce((n, p) => n + p.deals.length, 0);
  const chainN = chn.data?.counts.pending?.n ?? 0;
  const transfers = (chn.data?.rows ?? []).slice(0, CHAIN_GLANCE_ROWS);

  const states: ReadState[] = [sMcs, sSettle, sChain, ...(sAppr ? [sAppr] : [])];
  const allKnown = states.every((s) => s === 'known');
  const allWaiting = states.every((s) => s === 'waiting');
  const total = waiting.length + cheques.length + deals + chainN;

  return (
    <Panel
      title="Needs attention"
      aside={allKnown && total > 0 ? `${total} ${total === 1 ? 'item' : 'items'}` : undefined}
      action={role && <span className="rounded-md bg-panel-2 px-2 py-1 text-meta text-tg-hint">{role}</span>}
      flush
      className={className}
    >
      {allWaiting ? (
        <SkeletonRows rows={3} what="what needs you" />
      ) : allKnown && total === 0 ? (
        <EmptyState>Nothing needs you.</EmptyState>
      ) : (
        <div>
          {approvals && sAppr === 'failed' && <FailedRow what="approvals" error={approvals.error} retry={() => { void approvals.refetch(); }} />}
          {approvals && sAppr === 'waiting' && <WaitingRow what="approvals" />}
          {waiting.length > 0 && <ApprovalTask rows={waiting} stale={appr.stale} />}

          {sMcs === 'failed' && <FailedRow what="open cheques" error={mcs.error} retry={() => { void mcs.refetch(); }} />}
          {sMcs === 'waiting' && <WaitingRow what="open cheques" />}
          {toPay.length > 0 && <ChequeKind cheques={toPay} pay stale={chq.stale} />}
          {toDeliver.length > 0 && <ChequeKind cheques={toDeliver} pay={false} stale={chq.stale} />}

          {sSettle === 'failed' && <FailedRow what="settle-later trades" error={settle.error} retry={() => { void settle.refetch(); }} />}
          {sSettle === 'waiting' && <WaitingRow what="settle-later trades" />}
          {parties.length > 0 && (
            <>
              <FigureTaskRow
                icon={ArrowLeftRight}
                title={counted(stl.stale, deals, '1 deal to settle later', 'deals to settle later', 'Deals to settle later')}
                // Each name opens its party's page, whose Outstanding work is its settle-later trades (Settle later takes no
                // party filter).
                sub={<NameLinks
                  items={parties.map((p) => ({ key: p.slug, label: partyName({ display_name: p.display_name, slug: p.slug }), to: `/parties/${encodeURIComponent(p.slug)}` }))}
                  total={parties.length} more="/settle" />}
                action="Settle"
                to="/settle"
              />
            </>
          )}

          {sChain === 'failed' && <FailedRow what="the chain" error={chain.error} retry={() => { void chain.refetch(); }} />}
          {sChain === 'waiting' && <WaitingRow what="the chain" />}
          {chainN > 0 && (
            <>
              <FigureTaskRow
                icon={Link2}
                title={counted(chn.stale, chainN, 'Payment needs a match', 'payments need a match', 'Payments need a match')}
                // EACH TRANSFER, ONE TAP TO ITS ROW (operator, 14 Sept 2026; restored 1 Nov): the newest's figure in the row's
                // figure seat, its link and the rest's on the line under the title — never three figures in one sentence.
                sub={transfers[0] && (
                  <>
                    <Link to={`/chain?row=${encodeURIComponent(String(transfers[0].id))}`} className="text-tg-link hover:underline">Newest</Link>
                    {chainN > 1 && <> · <Link to="/chain" className="text-tg-link hover:underline">All {chainN}</Link></>}
                  </>
                )}
                figure={transfers[0] && <Money value={transfers[0].amount} asset={transfers[0].asset} />}
                action="Match"
                to={chainN === 1 && transfers[0] ? `/chain?row=${encodeURIComponent(String(transfers[0].id))}` : '/chain'}
              />
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

/** Custody moves waiting for a signature (only with custody switched on). An amount the stored transaction lacks is not
 *  drawn (never as 0). */
function ApprovalTask({ rows, stale }: { rows: ApprovalRow[]; stale: boolean }) {
  const first = rows[0]!;
  const lead = rows.length === 1 && first.kind === 'payout' ? 'Payout awaiting your signature' : null;
  return (
    <FigureTaskRow
      icon={Lock}
      tone="warn"
      title={lead ?? counted(stale, rows.length, '1 approval awaiting your signature', 'approvals awaiting your signature', 'Approvals awaiting your signature')}
      sub={first.partyName || undefined}
      figure={first.amount !== null ? <Money value={first.amount} asset={first.asset} /> : undefined}
      action="Review"
      to="/custody/approvals"
    />
  );
}

/** Open cheques of one kind — to pay the seller, or to deliver. The button opens the first; "+N more" opens the rest
 *  inline, each a link to its own cheque. */
function ChequeKind({ cheques, pay, stale }: { cheques: PendingMc[]; pay: boolean; stale: boolean }) {
  const [open, setOpen] = useState(false);
  const first = cheques[0]!;
  const name = (c: PendingMc) => partyName({ display_name: c.customer_name, slug: c.customer_slug });
  const amount = (c: PendingMc) => <Money value={c.mc_face_amount} asset="AED" />;
  const rest = cheques.slice(1);
  return (
    <>
      <FigureTaskRow
        icon={FileText}
        title={pay
          ? counted(stale, cheques.length, 'Cheque to pay', 'cheques to pay', 'Cheques to pay')
          : counted(stale, cheques.length, 'Cheque to deliver', 'cheques to deliver', 'Cheques to deliver')}
        sub={
          <>
            {name(first)}
            {rest.length > 0 && (
              <>
                {' · '}
                <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="text-tg-link hover:underline">
                  {open ? 'Less' : `+${rest.length} more`}
                </button>
              </>
            )}
          </>
        }
        figure={amount(first)}
        action={pay ? 'Pay' : 'Deliver'}
        to={`/mc/${encodeURIComponent(first.deal_id)}`}
      />
      {open && rest.map((c) => (
        <Link
          key={c.deal_id}
          to={`/mc/${encodeURIComponent(c.deal_id)}`}
          className="flex min-h-[44px] items-center gap-2 border-b border-stroke-soft py-2 pl-[60px] pr-4 text-meta last:border-b-0 hover:bg-panel-2 lg:min-h-[40px] lg:pl-[64px] lg:pr-5"
        >
          <span className="min-w-0 flex-1 break-words text-body text-tg-text">{name(c)}</span>
          <span className="shrink-0 whitespace-nowrap text-body">{amount(c)}</span>
        </Link>
      ))}
    </>
  );
}
